import { Component, ErrorInfo, ReactNode } from 'react';
import { Shield, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                <Shield className="w-10 h-10 text-red-500" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-dark-text mb-2">Something went wrong</h1>
            <p className="text-dark-muted mb-6">
              An unexpected error occurred. Please try refreshing the page.
            </p>

            {this.state.error && (
              <div className="mb-6 p-4 bg-dark-800 border border-white/[0.06] rounded-xl text-left">
                <p className="text-xs text-dark-muted mb-1">Error details:</p>
                <code className="text-sm text-red-400 break-all">
                  {this.state.error.message}
                </code>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={this.handleRefresh} className="btn-primary">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </button>
              <button onClick={this.handleGoHome} className="btn-secondary">
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
